import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../shared/UIComponents';
import { TrashIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EditableField from '../../shared/EditableField';

/**
 * ScoreAggregationRuleForm — authors a `score-aggregation` rule (the Academics
 * marks domain). Unlike the categorical rule forms which COUNT events, this one
 * describes a predicate over numeric scores: average / count / streak crossing a
 * threshold, scoped by subject and a time window, classified as support or
 * recognition (the mentions-vs-recognitions valency).
 *
 * The data it writes is validated and evaluated by the server rule processor:
 *   shape         'avg-below' | 'avg-above' | 'count-below' | 'count-above'
 *                 | 'streak-below' | 'streak-above'   (metric + direction)
 *   threshold     number — the line a score is below/above
 *   count         number — how many marks (count/streak shapes only)
 *   subjectScope  'same-subject' | 'all-subjects'
 *   timeframe     'academic-term' | 'academic-semester' | 'academic-year' | 'last-N-marks'
 *   timeCount     number — N for last-N-marks
 *   childDomain   'mentions' | 'recognitions'  — the valency
 */

const METRIC_OPTIONS = [
  { value: 'avg', label: 'average' },
  { value: 'count', label: 'count' },
  { value: 'streak', label: 'streak' },
];

const DIRECTION_OPTIONS = [
  { value: 'below', label: 'below' },
  { value: 'above', label: 'above' },
];

const SCOPE_OPTIONS = [
  { value: 'same-subject', label: 'the same subject' },
  { value: 'all-subjects', label: 'all subjects' },
];

const TIMEFRAME_OPTIONS = [
  { value: 'academic-term', label: 'the term' },
  { value: 'academic-semester', label: 'the semester' },
  { value: 'academic-year', label: 'the year' },
  { value: 'last-N-marks', label: 'recent marks' },
];

const VALENCY_OPTIONS = [
  { value: 'mentions', label: 'flag for support' },
  { value: 'recognitions', label: 'award recognition' },
];

const DEFAULTS = {
  title: 'Score Rule',
  shape: 'avg-below',
  threshold: 50,
  count: 3,
  subjectScope: 'same-subject',
  timeframe: 'academic-term',
  timeCount: 5,
  childDomain: 'mentions',
  active: true,
};

const hydrate = (rule) => {
  if (!rule) return { ...DEFAULTS };
  return {
    title: rule.title || rule.name || DEFAULTS.title,
    shape: rule.shape || DEFAULTS.shape,
    threshold: rule.threshold ?? DEFAULTS.threshold,
    count: rule.count ?? DEFAULTS.count,
    subjectScope: typeof rule.subjectScope === 'string' ? rule.subjectScope : DEFAULTS.subjectScope,
    timeframe: rule.timeframe || DEFAULTS.timeframe,
    timeCount: rule.timeCount ?? DEFAULTS.timeCount,
    childDomain: rule.childDomain || DEFAULTS.childDomain,
    active: rule.active !== undefined ? rule.active : DEFAULTS.active,
    ...(rule.id && { id: rule.id }),
  };
};

const ScoreAggregationRuleForm = ({ rule = null, ruleId = null, categoryKey = 'score-aggregation', onSave, onDelete }) => {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const prevRuleId = useRef(rule?.id);

  const [data, setData] = useState(() => hydrate(rule));
  const dataRef = useRef(data);
  const saveTail = useRef(Promise.resolve());
  const [saveError, setSaveError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const menuRef = useRef();

  // Re-hydrate when a different rule lands in this slot.
  useEffect(() => {
    if (rule?.id && rule.id !== prevRuleId.current) {
      prevRuleId.current = rule.id;
      dataRef.current = hydrate(rule);
      setData(dataRef.current);
    }
  }, [rule]);

  // Close the menu on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { save: debouncedSave } = useAutoSave(async (...args) => {
    // Keep saves in order so rapid edits use the last acknowledged revision.
    const pending = saveTail.current.then(() => onSave(...args));
    saveTail.current = pending.catch(() => {});
    try {
      await pending;
      setSaveError(null);
    } catch (error) {
      setSaveError(error.message || 'This rule could not be saved. Review it and try again.');
    }
  }, 100);

  const update = useCallback((updates) => {
    const current = dataRef.current;
    const newData = { ...current, ...updates };
    // React may defer a state updater. Build the save payload synchronously.
    if ('metric' in updates || 'direction' in updates) {
      const [m, d] = String(current.shape || 'avg-below').split('-');
      newData.shape = `${updates.metric ?? m}-${updates.direction ?? d}`;
      delete newData.metric;
      delete newData.direction;
    }
    dataRef.current = newData;
    setData(newData);
    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'score-aggregation', actualRuleId);
  }, [debouncedSave, categoryKey, ruleId, rule?.id]);

  // Sortable setup (matches the other rule forms).
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule?.id || 'temp' });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const [metric, direction] = String(data.shape || 'avg-below').split('-');
  const showMarkWindow = data.timeframe === 'last-N-marks';

  const MetricField = (
    <EditableField
      field="metric"
      value={metric}
      options={METRIC_OPTIONS}
      onUpdate={update}
      editingField={editingField}
      setEditingField={setEditingField}
    />
  );
  const DirectionField = (
    <EditableField
      field="direction"
      value={direction}
      options={DIRECTION_OPTIONS}
      onUpdate={update}
      editingField={editingField}
      setEditingField={setEditingField}
    />
  );
  const ThresholdField = (
    <EditableField
      field="threshold"
      value={data.threshold}
      type="number"
      onUpdate={update}
      editingField={editingField}
      setEditingField={setEditingField}
      validation={{ min: 0, max: 1000 }}
    />
  );
  const CountField = (
    <EditableField
      field="count"
      value={data.count}
      type="number"
      onUpdate={update}
      editingField={editingField}
      setEditingField={setEditingField}
      validation={{ min: 1, max: 999 }}
    />
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg className="w-4 h-4 text-base-content/50" fill="currentColor" viewBox="0 0 20 20">
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Editable title */}
          <div className="mb-3">
            <input
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || 'Score Rule';
                if (newTitle !== data.title) update({ title: newTitle });
                setEditingTitle(false);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className={`text-lg font-medium bg-transparent border-0 p-0 w-full outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-base pr-4">
              When a learner&rsquo;s {MetricField}
              {metric === 'avg' ? (
                <> score is {DirectionField} {ThresholdField}</>
              ) : (
                <> of scores {DirectionField} {ThresholdField} reaches {CountField}</>
              )}
              {' '}in {' '}
              <EditableField
                field="subjectScope"
                value={data.subjectScope}
                options={SCOPE_OPTIONS}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              {' '}over{' '}
              <EditableField
                field="timeframe"
                value={data.timeframe}
                options={TIMEFRAME_OPTIONS}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              {showMarkWindow && (
                <>
                  {' ('}
                  <EditableField
                    field="timeCount"
                    value={data.timeCount}
                    type="number"
                    onUpdate={update}
                    editingField={editingField}
                    setEditingField={setEditingField}
                    validation={{ min: 1, max: 99 }}
                  />
                  {' marks)'}
                </>
              )}
              , then{' '}
              <EditableField
                field="childDomain"
                value={data.childDomain}
                options={VALENCY_OPTIONS}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              .
            </div>

            <div className="flex items-center gap-2">
              {/* Active toggle */}
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                className={`toggle toggle-sm ${data.active ? 'toggle-success' : ''}`}
              />

              {/* Three-dots menu */}
              <div className="relative" ref={menuRef}>
                <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 hover:bg-base-200 rounded">
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>
                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel',
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) await onDelete(deleteId);
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <p className="mt-2 text-xs text-base-content/60 italic">
            {data.childDomain === 'recognitions'
              ? 'Recognises achievement — a positive follow-up when scores clear the line.'
              : 'Flags for support — a follow-up when scores fall short, so no learner slips through.'}
          </p>
          {saveError && <p role="alert" className="mt-2 text-sm text-error">Not saved: {saveError}</p>}
        </div>
      </div>

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default ScoreAggregationRuleForm;
